/**
 * components/admin/DepositRollupCard.jsx · v2.7.56+E33
 * ═════════════════════════════════════════════════════════════════
 * 7 / 30 / 90-day per-driver aggregate — sortable, flags drivers
 * with escalation-rate > 20% OR total_discrepancy > $50 so operating
 * partners can spot repeat-offenders during weekly ops reviews.
 *
 * GET /api/admin/deposits/rollup?days=7|30|90
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import adminApi from "@/lib/adminApi";
import { fmtUsd } from "@/lib/format";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, AlertTriangle, ChevronDown, ChevronUp, RefreshCw, Users, DollarSign,
} from "lucide-react";

const WINDOWS = [7, 30, 90];

export default function DepositRollupCard() {
  const [days, setDays] = useState(30);
  const [expanded, setExpanded] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("discrepancy"); // discrepancy | escalation | submitted

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/deposits/rollup?days=${days}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Rollup load failed");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const sortedDrivers = useMemo(() => {
    if (!data?.drivers) return [];
    const arr = [...data.drivers];
    if (sortBy === "escalation") arr.sort((a, b) => b.escalation_rate - a.escalation_rate);
    else if (sortBy === "submitted") arr.sort((a, b) => b.total_submitted - a.total_submitted);
    else arr.sort((a, b) => b.total_discrepancy - a.total_discrepancy);
    return arr;
  }, [data, sortBy]);

  const totals = data?.totals || {};

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-4 mb-4" data-testid="deposit-rollup-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-2 hover:opacity-80 transition"
          data-testid="rollup-toggle"
        >
          <div className="w-9 h-9 rounded-full bg-sky-500/15 border border-sky-400/40 flex items-center justify-center">
            <TrendingUp className="h-4.5 w-4.5 text-sky-300"/>
          </div>
          <div className="text-left">
            <div className="text-sm font-bold text-zinc-100">Deposit Rollup · {days}-day</div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-widest">Per-driver variance leaderboard</div>
          </div>
          {expanded ? <ChevronUp className="h-4 w-4 text-zinc-500"/> : <ChevronDown className="h-4 w-4 text-zinc-500"/>}
        </button>

        <div className="flex items-center gap-2 flex-wrap">
          {WINDOWS.map((n) => (
            <button
              key={n}
              onClick={() => setDays(n)}
              className={`px-2.5 py-1 rounded text-[11px] font-bold uppercase tracking-wider border transition ${
                days === n
                  ? "bg-sky-400 border-sky-400 text-zinc-950"
                  : "bg-zinc-950 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              }`}
              data-testid={`rollup-window-${n}`}
            >
              {n}d
            </button>
          ))}
          <Button size="sm" variant="outline" onClick={load} disabled={loading} className="h-7 text-[11px]" data-testid="rollup-refresh">
            <RefreshCw className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`}/>Reload
          </Button>
        </div>
      </div>

      {expanded && (
        <>
          {/* Aggregate tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
            <Tile
              label="Drivers"
              value={totals.driver_count ?? 0}
              icon={<Users className="h-3 w-3"/>}
              testid="rollup-driver-count"
            />
            <Tile
              label="Submitted"
              value={fmtUsd(totals.total_submitted ?? 0)}
              icon={<DollarSign className="h-3 w-3"/>}
              tone="emerald"
              testid="rollup-total-submitted"
            />
            <Tile
              label="Verified"
              value={fmtUsd(totals.total_verified ?? 0)}
              icon={<DollarSign className="h-3 w-3"/>}
              testid="rollup-total-verified"
            />
            <Tile
              label="Variance"
              value={fmtUsd(totals.total_discrepancy ?? 0)}
              icon={<TrendingDown className="h-3 w-3"/>}
              tone={totals.total_discrepancy > 0 ? "red" : "zinc"}
              testid="rollup-total-variance"
            />
            <Tile
              label="Flagged"
              value={`${totals.flagged_drivers ?? 0} / ${totals.driver_count ?? 0}`}
              icon={<AlertTriangle className="h-3 w-3"/>}
              tone={(totals.flagged_drivers ?? 0) > 0 ? "amber" : "zinc"}
              testid="rollup-flagged"
            />
          </div>

          {/* Table */}
          {loading ? (
            <div className="p-6 text-center text-xs text-zinc-500" data-testid="rollup-loading">Loading rollup…</div>
          ) : sortedDrivers.length === 0 ? (
            <div className="p-6 text-center text-xs text-zinc-500 border border-dashed border-zinc-800 rounded" data-testid="rollup-empty">
              No deposits submitted in the last {days} days.
            </div>
          ) : (
            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-xs" data-testid="rollup-table">
                <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
                  <tr>
                    <th className="text-left px-1.5 py-1.5">Driver</th>
                    <th className="text-right px-1.5 py-1.5">Rows</th>
                    <th className="text-right px-1.5 py-1.5">
                      <SortBtn label="Submitted" active={sortBy === "submitted"} onClick={() => setSortBy("submitted")} testid="rollup-sort-submitted"/>
                    </th>
                    <th className="text-right px-1.5 py-1.5">Verified</th>
                    <th className="text-right px-1.5 py-1.5">Tips</th>
                    <th className="text-right px-1.5 py-1.5">
                      <SortBtn label="Variance" active={sortBy === "discrepancy"} onClick={() => setSortBy("discrepancy")} testid="rollup-sort-discrepancy"/>
                    </th>
                    <th className="text-right px-1.5 py-1.5">
                      <SortBtn label="Escalation" active={sortBy === "escalation"} onClick={() => setSortBy("escalation")} testid="rollup-sort-escalation"/>
                    </th>
                    <th className="text-center px-1.5 py-1.5">Last Shift</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDrivers.map((d) => (
                    <RollupRow key={d.driver_id} row={d}/>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function Tile({ label, value, icon, tone, testid }) {
  const t = tone === "emerald" ? "text-emerald-300"
    : tone === "red" ? "text-red-300"
    : tone === "amber" ? "text-amber-300"
    : "text-zinc-100";
  return (
    <div className="p-2 rounded bg-zinc-950 border border-zinc-800" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
        {icon}{label}
      </div>
      <div className={`meter-display font-bold text-base mt-0.5 ${t}`}>{value}</div>
    </div>
  );
}

function SortBtn({ label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 hover:text-zinc-200 transition ${active ? "text-sky-300 font-bold" : ""}`}
      data-testid={testid}
    >
      {label}{active && " ↓"}
    </button>
  );
}

function RollupRow({ row }) {
  const isFlagged = !!row.alert;
  const rate = row.escalation_rate;
  const ratePct = (rate * 100).toFixed(1);
  const rateBadge = rate > 0.20
    ? "bg-red-500/15 text-red-300 border-red-400/40"
    : rate > 0.10
    ? "bg-amber-500/15 text-amber-300 border-amber-400/40"
    : "bg-emerald-500/15 text-emerald-300 border-emerald-400/40";
  return (
    <tr
      className={`border-t border-zinc-800 ${isFlagged ? "bg-red-500/5" : "hover:bg-zinc-800/30"}`}
      data-testid={`rollup-row-${row.driver_id}`}
    >
      <td className="px-1.5 py-2">
        <div className="flex items-center gap-1.5">
          {isFlagged && <AlertTriangle className="h-3 w-3 text-red-400 flex-shrink-0" title="Flagged: escalation-rate > 20% OR variance > $50"/>}
          <div>
            <div className="font-semibold text-zinc-100">{row.driver_name || row.driver_email?.split("@")[0]}</div>
            <div className="text-[10px] text-zinc-500">{row.driver_email}</div>
          </div>
        </div>
      </td>
      <td className="px-1.5 py-2 text-right meter-display">{row.row_count}</td>
      <td className="px-1.5 py-2 text-right meter-display">{fmtUsd(row.total_submitted)}</td>
      <td className="px-1.5 py-2 text-right meter-display text-emerald-300">{fmtUsd(row.total_verified)}</td>
      <td className="px-1.5 py-2 text-right meter-display text-amber-300">{fmtUsd(row.total_tips)}</td>
      <td className={`px-1.5 py-2 text-right meter-display font-bold ${row.total_discrepancy > 0 ? "text-red-300" : "text-zinc-300"}`}>
        {fmtUsd(row.total_discrepancy)}
      </td>
      <td className="px-1.5 py-2 text-right">
        <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border ${rateBadge}`}>
          {row.escalated_count}/{row.row_count} · {ratePct}%
        </span>
      </td>
      <td className="px-1.5 py-2 text-center text-zinc-400 text-[11px]">{row.last_shift_date || "—"}</td>
    </tr>
  );
}
